import React, { useState, useEffect } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

export default function ClientPassengerLimits({ user, operatorDetails }) {
    const [clients, setClients] = useState([]);
    const [subscription, setSubscription] = useState(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    
    // Store original and modified limits
    const [limits, setLimits] = useState({});

    useEffect(() => {
        const fetchData = async () => {
            if (!operatorDetails || !operatorDetails._id) return;
            try {
                // Fetch Subscription to get the total passenger limit
                const subRes = await axios.get(`/api/subscriptions/operator/${operatorDetails._id}`);
                let activeSub = null;
                if (subRes.data && subRes.data.length > 0) {
                    activeSub = subRes.data.find(s => s.status === 'Active') || subRes.data[0];
                    setSubscription(activeSub);
                }

                // Fetch Clients
                const clientsRes = await axios.get(`/api/clients/${operatorDetails._id}`);
                setClients(clientsRes.data);
                
                // Initialize limits map
                const limitsMap = {};
                clientsRes.data.forEach(c => {
                    limitsMap[c._id] = c.passengerLimit || 0;
                });
                setLimits(limitsMap);

            } catch (err) {
                console.error("Failed to fetch data:", err);
            } finally {
                setLoading(false);
            }
        };
        fetchData();
    }, [operatorDetails]);

    const totalAllowed = subscription?.planId?.passengerLimit || -1;
    const isUnlimited = totalAllowed === -1;

    const currentTotalAllocated = Object.values(limits).reduce((acc, val) => acc + (Number(val) || 0), 0);
    const remainingLimit = isUnlimited ? 'Unlimited' : totalAllowed - currentTotalAllocated;
    const isExceeded = !isUnlimited && currentTotalAllocated > totalAllowed;

    const handleLimitChange = (clientId, value) => {
        const numValue = value === '' ? 0 : Number(value);
        if (numValue < 0) return; // Prevent negative limits
        setLimits(prev => ({ ...prev, [clientId]: numValue }));
    };

    const handleSave = async () => {
        if (isExceeded) {
            alert(`Total allocated limits (${currentTotalAllocated}) cannot exceed your allowed limit (${totalAllowed}).`);
            return;
        }

        setSaving(true);
        try {
            const payload = {
                limits: Object.keys(limits).map(clientId => ({
                    clientId,
                    passengerLimit: limits[clientId]
                }))
            };
            await axios.put('/api/clients/bulk/passenger-limits', payload);
            alert("Passenger limits updated successfully.");
        } catch (err) {
            console.error("Failed to save passenger limits:", err);
            alert("An error occurred while saving.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100%', color: '#6b7280', padding: 40 }}>
                Loading Passenger Limits...
            </div>
        );
    }

    return (
        <div className="op-container" style={{ padding: '30px', maxWidth: '1200px', margin: '0 auto', fontFamily: 'Inter, sans-serif' }}>
            <header className="op-header" style={{ marginBottom: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div className="op-header-left">
                    <h1 className="op-title" style={{ fontSize: '24px', fontWeight: 700, margin: '0 0 8px 0', color: '#0f172a' }}>Client Passenger Limits</h1>
                    <p className="op-subtitle" style={{ color: '#64748b', margin: 0, fontSize: '14px' }}>Distribute your total passenger limit among your clients.</p>
                </div>
                <button 
                    onClick={handleSave} 
                    disabled={saving || isExceeded}
                    style={{
                        padding: '10px 24px',
                        background: (saving || isExceeded) ? '#94a3b8' : '#10b981',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        fontWeight: 600,
                        cursor: (saving || isExceeded) ? 'not-allowed' : 'pointer',
                        transition: 'background 0.2s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        boxShadow: '0 4px 6px rgba(16, 185, 129, 0.2)'
                    }}
                >
                    {saving ? 'Saving...' : 'Save Changes'}
                </button>
            </header>

            {/* Summary Banner */}
            <div style={{
                background: isExceeded ? '#fef2f2' : '#f8fafc',
                border: `1px solid ${isExceeded ? '#fca5a5' : '#e2e8f0'}`,
                borderRadius: '12px',
                padding: '24px',
                marginBottom: '30px',
                display: 'flex',
                gap: '40px',
                alignItems: 'center'
            }}>
                <div>
                    <div style={{ fontSize: '12px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '4px' }}>Total Plan Limit</div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: '#0f172a' }}>
                        {isUnlimited ? 'Unlimited' : totalAllowed}
                    </div>
                </div>
                <div style={{ width: '1px', height: '40px', background: isExceeded ? '#fca5a5' : '#cbd5e1' }}></div>
                <div>
                    <div style={{ fontSize: '12px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '4px' }}>Total Allocated</div>
                    <div style={{ fontSize: '28px', fontWeight: 800, color: isExceeded ? '#dc2626' : '#3b82f6' }}>
                        {currentTotalAllocated}
                    </div>
                </div>
                {!isUnlimited && (
                    <>
                        <div style={{ width: '1px', height: '40px', background: isExceeded ? '#fca5a5' : '#cbd5e1' }}></div>
                        <div>
                            <div style={{ fontSize: '12px', textTransform: 'uppercase', color: '#64748b', fontWeight: 600, letterSpacing: '0.5px', marginBottom: '4px' }}>Remaining</div>
                            <div style={{ fontSize: '28px', fontWeight: 800, color: isExceeded ? '#dc2626' : '#10b981' }}>
                                {remainingLimit}
                            </div>
                        </div>
                    </>
                )}
            </div>

            {/* Clients Table */}
            <div style={{ background: '#fff', borderRadius: '12px', border: '1px solid #e2e8f0', overflow: 'hidden', boxShadow: '0 4px 6px -1px rgba(0,0,0,0.05)' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
                    <thead style={{ background: '#f8fafc', borderBottom: '1px solid #e2e8f0' }}>
                        <tr>
                            <th style={{ padding: '16px 24px', fontSize: '13px', color: '#475569', fontWeight: 600, textTransform: 'uppercase' }}>Client Name</th>
                            <th style={{ padding: '16px 24px', fontSize: '13px', color: '#475569', fontWeight: 600, textTransform: 'uppercase' }}>Industry</th>
                            <th style={{ padding: '16px 24px', fontSize: '13px', color: '#475569', fontWeight: 600, textTransform: 'uppercase' }}>Current Passengers</th>
                            <th style={{ padding: '16px 24px', fontSize: '13px', color: '#475569', fontWeight: 600, textTransform: 'uppercase' }}>Allocated Limit</th>
                        </tr>
                    </thead>
                    <tbody>
                        {clients.length === 0 ? (
                            <tr>
                                <td colSpan="4" style={{ padding: '40px', textAlign: 'center', color: '#64748b' }}>No clients found. Add some clients first.</td>
                            </tr>
                        ) : (
                            clients.map(client => {
                                const currentCount = client.contacts ? client.contacts.length : 0;
                                return (
                                    <tr key={client._id} style={{ borderBottom: '1px solid #f1f5f9' }}>
                                        <td style={{ padding: '16px 24px', fontWeight: 600, color: '#0f172a' }}>{client.companyName}</td>
                                        <td style={{ padding: '16px 24px', color: '#64748b', fontSize: '14px' }}>{client.industry || '—'}</td>
                                        <td style={{ padding: '16px 24px', color: '#64748b', fontSize: '14px' }}>
                                            <span style={{ 
                                                display: 'inline-block', 
                                                padding: '4px 10px', 
                                                background: '#f1f5f9', 
                                                borderRadius: '20px', 
                                                fontWeight: 600,
                                                color: currentCount > limits[client._id] ? '#dc2626' : '#475569'
                                            }}>
                                                {currentCount} Passengers
                                            </span>
                                        </td>
                                        <td style={{ padding: '16px 24px' }}>
                                            <input 
                                                type="number" 
                                                min="0"
                                                value={limits[client._id] || 0} 
                                                onChange={(e) => handleLimitChange(client._id, e.target.value)}
                                                style={{
                                                    width: '100px',
                                                    padding: '8px 12px',
                                                    borderRadius: '6px',
                                                    border: `1px solid ${isExceeded ? '#fca5a5' : '#cbd5e1'}`,
                                                    outline: 'none',
                                                    fontSize: '14px',
                                                    fontWeight: 600,
                                                    color: '#0f172a'
                                                }}
                                            />
                                        </td>
                                    </tr>
                                );
                            })
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}
